// @ts-check

import { html } from "lit-html";
import {
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
  renderCheckboxField,
  renderSectionCard,
  renderSteppedNumberField,
} from "../ui.js";
import { copyTextToClipboard, hasText, parseNumber } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createRandomNumberGeneratorState>} RandomNumberGeneratorState */
/** @typedef {{ key: string, label: string, min: number, max: number }} RangePreset */

/** @type {RangePreset[]} */
const RANGE_PRESETS = [
  { key: "1-6", label: "Dice 1-6", min: 1, max: 6 },
  { key: "0-9", label: "0-9", min: 0, max: 9 },
  { key: "1-10", label: "1-10", min: 1, max: 10 },
  { key: "0-100", label: "0-100", min: 0, max: 100 },
  { key: "0-1000", label: "0-1000", min: 0, max: 1000 },
];
const DEFAULT_DIGITS = "8";
const MIN_DIGITS = 1;
const MAX_DIGITS = 15;
const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";

export const randomNumberGeneratorTool = {
  key: "random-number-generator",
  path: "/random-number-generator",
  title: "Random Number Generator",
  blurb: "Generate digit-based random numbers or quick preset ranges.",
  badge: "Random",
  accentClass: "bg-gradient-to-r from-amber-300 via-lime-300 to-emerald-300",
  createState: createRandomNumberGeneratorState,
  render: renderRandomNumberGeneratorPage,
};

function createRandomNumberGeneratorState() {
  const digits = DEFAULT_DIGITS;
  const allowLeadingZeros = true;
  const result = generateDigitNumber(digits, allowLeadingZeros);

  return {
    digits,
    allowLeadingZeros,
    activePreset: "digits",
    result,
    copyStatus: "idle",
  };
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderRandomNumberGeneratorPage(toolState, env) {
  const digitValidation = validateDigits(toolState.digits);
  const hasActivePreset = toolState.activePreset !== "digits";
  const isGenerateDisabled =
    !hasActivePreset && digitValidation.kind === "error";
  const canCopy = hasText(toolState.result);
  const copyButtonClass = getCopyButtonClass(toolState.copyStatus);
  const copyButtonLabel = getCopyButtonLabel(toolState.copyStatus);

  return html`
    <section class="grid min-h-[calc(100dvh-8rem)] content-start gap-4">
      ${renderSectionCard(
        "Generator",
        "",
        html`
          <div class="grid gap-4">
            <div
              class="min-w-0 rounded-lg bg-stone-100 px-4 py-5 text-center sm:px-6 sm:py-7"
            >
              <div class="text-xs font-semibold uppercase text-stone-400">
                ${getResultLabel(toolState.activePreset)}
              </div>
              <output
                class="mt-2 block break-all font-mono text-5xl font-semibold text-stone-950 sm:text-7xl"
                >${toolState.result}</output>
            </div>

            <div class="grid gap-4 sm:grid-cols-[14rem_1fr] sm:items-end">
              ${renderSteppedNumberField({
                label: "Digits",
                value: toolState.digits,
                placeholder: DEFAULT_DIGITS,
                onInput: function (event) {
                  env.handleFieldInput(toolState, "digits", event);
                },
                onStepDown: function () {
                  stepDigits(toolState, env, -1);
                },
                onStepUp: function () {
                  stepDigits(toolState, env, 1);
                },
              })}
              ${renderCheckboxField({
                label: "Allow leading zeros",
                checked: Boolean(toolState.allowLeadingZeros),
                help: "When enabled, digit mode pads the result to the selected length.",
                onInput: function (event) {
                  env.handleCheckboxInput(
                    toolState,
                    "allowLeadingZeros",
                    event,
                  );
                },
              })}
            </div>

            ${renderPresetButtons(toolState, env)}

            <div class="flex flex-wrap gap-3">
              <button
                class=${PRIMARY_BUTTON_CLASS}
                type="button"
                ?disabled=${isGenerateDisabled}
                @click=${function () {
                  generateCurrentResult(toolState, env);
                }}
              >
                Generate
              </button>
              <button
                class=${copyButtonClass}
                type="button"
                ?disabled=${!canCopy}
                @click=${async function () {
                  await copyResult(toolState, env);
                }}
              >
                <span class=${ICON_BUTTON_CONTENT_CLASS}>
                  ${renderCopyIcon()}
                  <span>${copyButtonLabel}</span>
                </span>
              </button>
            </div>

            ${digitValidation.kind === "error"
              ? html`<p class="text-sm font-medium text-red-600">
                  ${digitValidation.error}
                </p>`
              : ""}
          </div>
        `,
      )}
    </section>
  `;
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderPresetButtons(toolState, env) {
  /** @type {LitView[]} */
  const presetButtons = [];
  for (let i = 0, total = RANGE_PRESETS.length; i < total; i += 1) {
    const preset = RANGE_PRESETS[i];
    const isActive = toolState.activePreset === preset.key;
    const buttonClass = isActive
      ? PRIMARY_BUTTON_CLASS
      : SECONDARY_BUTTON_CLASS;

    presetButtons.push(html`
      <button
        class=${buttonClass}
        type="button"
        @click=${function () {
          toggleRangePreset(toolState, env, preset);
        }}
      >
        ${preset.label}
      </button>
    `);
  }

  return html` <div class="grid grid-cols-3 gap-2">${presetButtons}</div> `;
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {void}
 */
function generateCurrentResult(toolState, env) {
  const activePreset = findRangePreset(toolState.activePreset);
  if (activePreset) {
    generateRangeResult(toolState, env, activePreset);
    return;
  }

  const digitValidation = validateDigits(toolState.digits);
  if (digitValidation.kind === "error") {
    return;
  }

  toolState.activePreset = "digits";
  toolState.result = generateDigitNumber(
    toolState.digits,
    Boolean(toolState.allowLeadingZeros),
  );
  toolState.copyStatus = "idle";
  env.scheduleRender();
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @param {RangePreset} preset
 * @returns {void}
 */
function toggleRangePreset(toolState, env, preset) {
  if (toolState.activePreset === preset.key) {
    toolState.activePreset = "digits";
    generateCurrentResult(toolState, env);
    return;
  }

  generateRangeResult(toolState, env, preset);
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @param {RangePreset} preset
 * @returns {void}
 */
function generateRangeResult(toolState, env, preset) {
  toolState.activePreset = preset.key;
  toolState.result = String(generateRandomInteger(preset.min, preset.max));
  toolState.copyStatus = "idle";
  env.scheduleRender();
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @param {number} direction
 * @returns {void}
 */
function stepDigits(toolState, env, direction) {
  const currentDigits = getDigitCount(toolState.digits);
  const nextDigits = Math.min(
    MAX_DIGITS,
    Math.max(MIN_DIGITS, currentDigits + direction),
  );
  toolState.digits = String(nextDigits);
  toolState.activePreset = "digits";
  toolState.copyStatus = "idle";
  env.scheduleRender();
}

/**
 * @param {RandomNumberGeneratorState} toolState
 * @param {ToolEnv} env
 * @returns {Promise<void>}
 */
async function copyResult(toolState, env) {
  const clipboardText = toolState.result.trim();
  if (!hasText(clipboardText)) {
    return;
  }

  try {
    await copyTextToClipboard(clipboardText);
    env.setTemporaryStatus(
      toolState,
      "copyStatus",
      "copied",
      "idle",
      "randomNumberGenerator",
    );
  } catch (error) {
    console.error("[app] copy random number failed", error);
    env.setTemporaryStatus(
      toolState,
      "copyStatus",
      "error",
      "idle",
      "randomNumberGenerator",
    );
  }
}

/**
 * @param {string} key
 * @returns {RangePreset | null}
 */
function findRangePreset(key) {
  let result = /** @type {RangePreset | null} */ (null);
  for (let i = 0, total = RANGE_PRESETS.length; i < total; i += 1) {
    const preset = RANGE_PRESETS[i];
    if (preset.key === key) {
      result = preset;
    }
  }

  return result;
}

/**
 * @param {string} activePreset
 * @returns {string}
 */
function getResultLabel(activePreset) {
  let label = "Digit result";
  if (activePreset !== "digits") {
    label = `Range ${activePreset}`;
  }

  return label;
}

/**
 * @param {string} digitsText
 * @param {boolean} allowLeadingZeros
 * @returns {string}
 */
function generateDigitNumber(digitsText, allowLeadingZeros) {
  const digits = getDigitCount(digitsText);
  const maxExclusive = 10 ** digits;
  const randomNumber = Math.floor(Math.random() * maxExclusive);
  let result = String(randomNumber);
  if (allowLeadingZeros) {
    result = result.padStart(digits, "0");
  }

  return result;
}

/**
 * @param {string} digitsText
 * @returns {number}
 */
function getDigitCount(digitsText) {
  let digits = Number(DEFAULT_DIGITS);
  const parsedDigits = parseNumber(digitsText);
  if (Number.isFinite(parsedDigits)) {
    digits = Math.trunc(parsedDigits);
  }

  return digits;
}

/**
 * @param {string} digitsText
 * @returns {{ kind: "ready" } | { kind: "error", error: string }}
 */
function validateDigits(digitsText) {
  let result =
    /** @type {{ kind: "ready" } | { kind: "error", error: string }} */ ({
      kind: "ready",
    });
  const digits = getDigitCount(digitsText);
  if (!hasText(digitsText) || digits < MIN_DIGITS || digits > MAX_DIGITS) {
    result = {
      kind: "error",
      error: `Enter ${MIN_DIGITS} to ${MAX_DIGITS} digits.`,
    };
  }

  return result;
}

/**
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function generateRandomInteger(min, max) {
  const rangeSize = max - min + 1;
  const randomOffset = Math.floor(Math.random() * rangeSize);

  return min + randomOffset;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  `;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonClass(copyStatus) {
  let result = SECONDARY_BUTTON_CLASS;
  if (copyStatus === "copied") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-emerald-700 px-4 text-sm font-semibold text-white";
  }

  if (copyStatus === "error") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonLabel(copyStatus) {
  let result = "Copy";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}
